import { useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import WixEntryLoader from './WixEntryLoader.js';
import {
  consumeSingleAgentEntry,
  isSingleAgentEntryPending,
  shouldEnterSingleAgent,
} from '../utils/singleAgentEntry.js';

// Sends a user who owns exactly one agent straight into that agent after
// sign-in, instead of parking them on a /my-bots list with a single card. The
// mirror image of NoAgentsRedirect (zero agents → /my-bots); both live inside
// SelectedBotProvider and both decide on the loaded bot list, never on a
// mid-fetch empty one.
//
// It GATES the shell rather than only redirecting from it: the decision needs
// the bot list, which arrives a fetch later, so a plain redirect would paint
// the very one-card list we mean to skip and then yank it away. While the
// decision is pending this holds the same boot loader AppShell shows, so
// sign-in flows continuously into the destination.
//
// Only engages when a sign-in entry point armed the one-shot flag
// (markSingleAgentEntry) — see utils/singleAgentEntry.js for why /my-bots must
// stay reachable by deliberate navigation.
function SingleAgentEntryGate({ isWixSite, children }) {
  const { bots, loading } = useSelectedBot();
  const location = useLocation();
  const navigate = useNavigate();

  const [decided, setDecided] = useState(false);
  // PEEKED, never spent, on every render: signing in tears this component down
  // and back up (AppShell's validate_token effect re-runs on isAuthenticated and
  // flips `loading`, App.js:601,:644), so a flag claimed at mount would be
  // burned by the mount that gets discarded and the skip would never happen.
  const pending = !decided && isSingleAgentEntryPending();

  // useLayoutEffect, not useEffect: this runs before the browser paints, so the
  // render that first sees the loaded bot list never reaches the screen as
  // /my-bots — it is replaced by /overview in the same frame.
  useLayoutEffect(() => {
    if (decided || loading) return;
    // Spend the flag HERE — the one moment the decision can actually be made.
    if (!consumeSingleAgentEntry()) return;
    setDecided(true);
    if (shouldEnterSingleAgent({ pathname: location.pathname, bots })) {
      // The single agent is already the selected one: SelectedBotProvider's
      // reconcileSelectedBotId falls back to bots[0], so /overview opens on it.
      navigate('/overview', { replace: true });
    }
  }, [decided, loading, bots, location.pathname, navigate]);

  if (pending && location.pathname === '/my-bots') {
    // Same two loaders AppShell boots with — the purple one for a Wix-embedded
    // session so it stays continuous with the onboarding, else the portal
    // spinner (App.js:831-844).
    if (isWixSite) return <WixEntryLoader />;
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return children;
}

export default SingleAgentEntryGate;
